import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, HandCoins, ExternalLink, FileText, Printer, Link2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Money, StatusPill, AuthImage, WaStatus } from "@/components/common";
import { Stars } from "@/components/RateDelivery";
import { PaymentDialog } from "@/components/PaymentDialog";
import { PauseManager } from "@/components/PauseManager";
import { LocationTools } from "@/components/LocationTools";
import { CustomerControls } from "@/components/CustomerControls";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, itemsText, portalUrl, waLink, msgs, todayIST, shiftDate } from "@/lib/format";

const MSG_KIND = { order_ask: "Tomorrow's order question", weekly_bill: "Weekly bill", reminder: "Payment reminder", owner_alert: "Credit alert" };

function Statement({ cid }) {
  const [end, setEnd] = useState(todayIST());
  const [start, setStart] = useState(shiftDate(todayIST(), -6));
  const [st, setSt] = useState(null);
  useEffect(() => { api.get(`/customers/${cid}/statement`, { params: { start, end } }).then((r) => setSt(r.data)); }, [cid, start, end]);
  if (!st) return null;
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="statement-panel">
      <div className="flex flex-wrap items-end gap-3 no-print">
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="statement-start" />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="statement-end" />
        <Button variant="outline" onClick={() => window.print()} data-testid="statement-print"><Printer className="mr-1 h-4 w-4" />Print</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-5">
        {[["Opening", st.opening], ["Sales", st.total_debit], ["Paid", st.total_credit], ["Closing", st.closing]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-[#FAF4E8] p-3"><div className="text-[11px] uppercase text-muted-foreground">{l}</div><Money value={v} className="font-semibold" /></div>
        ))}
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Details</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
        <TableBody>
          {st.entries.map((e) => (
            <TableRow key={e.id}><TableCell>{fmtDate(e.date)}</TableCell><TableCell>{e.note}</TableCell>
              <TableCell className="text-right">{e.debit ? <Money value={e.debit} /> : ""}</TableCell>
              <TableCell className="text-right text-emerald-700">{e.credit ? <Money value={e.credit} /> : ""}</TableCell>
              <TableCell className="text-right"><Money value={e.balance} className="font-semibold" /></TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [pay, setPay] = useState(null);
  const [biz, setBiz] = useState("");
  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/settings").then((r) => setBiz(r.data.business_name)); }, [load]);
  if (!c) return <div className="h-64 animate-pulse rounded-3xl bg-white" />;
  const geocode = async () => {
    try { const { data } = await api.post(`/customers/${c.id}/geocode`); toast.success("Location found on the map"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rzpLink = async () => {
    try {
      const { data } = await api.post("/payment-links", { customer_id: c.id });
      navigator.clipboard?.writeText(data.short_url);
      toast.success(`Razorpay link copied: ${data.short_url}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center text-sm text-muted-foreground hover:text-[#0F382C] mb-4" data-testid="back-to-customers"><ArrowLeft className="mr-1 h-4 w-4" />Customers</Link>
      <PageHeader eyebrow={`${c.area} · ${c.phone}`} title={c.name} subtitle={c.address}>
        <Button variant="outline" asChild><a href={portalUrl(c.token)} target="_blank" rel="noreferrer" data-testid="open-customer-portal"><ExternalLink className="mr-1 h-4 w-4" />Customer page</a></Button>
        <Button className="bg-[#25D366] hover:bg-[#20bd5a] text-white" onClick={() => window.open(waLink(c.phone, msgs.payment(c, c.balance, biz)), "_blank")} data-testid="send-payment-link-btn"><MessageCircle className="mr-1 h-4 w-4" />Send payment link</Button>
        <Button variant="outline" onClick={rzpLink} data-testid="razorpay-link-btn"><Link2 className="mr-1 h-4 w-4" />Razorpay link</Button>
        <Button variant="outline" onClick={geocode} data-testid="geocode-btn"><MapPin className="mr-1 h-4 w-4" />{c.lat ? "Update map location" : "Find on map"}</Button>
        <Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setPay(c)} data-testid="record-payment-btn"><HandCoins className="mr-1 h-4 w-4" />Record payment</Button>
      </PageHeader>
      <CustomerControls c={c} onChanged={load} />
      <div className="mb-6"><LocationTools who={c} saveUrl={`/customers/${c.id}/maps-link`} onSaved={load} biz={biz} prefix="customer" /></div>
      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <div className={`rounded-r-2xl border-l-4 p-5 shadow-sm ${c.balance > c.credit_limit ? "border-l-rose-500 bg-rose-50" : "border-l-amber-500 bg-amber-50/60"}`} data-testid="customer-balance-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Outstanding</div>
          <Money value={c.balance} className="text-3xl font-semibold block mt-2" testid="customer-balance" />
          {c.balance > c.credit_limit && <div className="text-xs text-rose-700 mt-1">Over credit limit</div>}
        </div>
        <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Credit limit</div><Money value={c.credit_limit} className="text-2xl font-semibold block mt-2" /></div>
        <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Orders (last 60)</div><div className="font-mono text-2xl font-semibold mt-2">{c.orders.filter((o) => o.status === "confirmed").length}</div></div>
      </div>
      <div className="mb-8 max-w-xl rounded-2xl border border-sky-200 bg-white p-5" data-testid="customer-holiday-section">
        <div className="font-display font-semibold text-[#0F382C] mb-3">Holiday pause</div>
        <PauseManager pauses={(c.pauses || []).filter((p) => p.end >= todayIST())} prefix="admin-pause"
          onAdd={async (start, end) => { try { await api.post(`/customers/${c.id}/pauses`, { start, end }); toast.success("Holiday saved"); load(); } catch (e) { toast.error(errMsg(e)); } }}
          onRemove={async (pid) => { await api.delete(`/customers/${c.id}/pauses/${pid}`); toast.success("Holiday removed"); load(); }} />
      </div>
      <Tabs defaultValue="orders">
        <TabsList className="bg-[#FAF4E8]">
          <TabsTrigger value="orders" data-testid="tab-orders">Orders & deliveries</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement">Weekly statement</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">WhatsApp messages</TabsTrigger>
        </TabsList>
        <TabsContent value="orders">
          <div className="space-y-3">
            {c.orders.length === 0 && <div className="text-sm text-muted-foreground p-6">No orders yet.</div>}
            {c.orders.map((o) => (
              <div key={o.id} className="flex flex-col gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-4 sm:flex-row sm:items-center" data-testid={`customer-order-${o.id}`}>
                {o.photo_path ? <AuthImage path={`/deliveries/${o.id}/photo`} className="h-16 w-16 rounded-xl" /> : <div className="h-16 w-16 rounded-xl bg-[#FAF4E8]" />}
                <div className="flex-1">
                  <div className="font-semibold">{fmtDate(o.date)} {o.invoice_no && <span className="text-xs text-muted-foreground font-mono">{o.invoice_no}</span>}</div>
                  <div className="text-sm text-muted-foreground">{o.status === "skipped" ? "No batter needed" : itemsText(o.items)}</div>
                  {o.rating && <div className="mt-1 flex items-center gap-2 text-xs" data-testid={`customer-rating-${o.id}`}><Stars value={o.rating.stars} size="h-3.5 w-3.5" /><span className="text-muted-foreground">{[...(o.rating.tags || []), o.rating.comment].filter(Boolean).join(" · ")}</span></div>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {o.status === "confirmed" && <StatusPill status={o.delivery_status} />}
                  {o.status === "skipped" && <StatusPill status="skipped" label="No need" />}
                  {o.customer_confirmation && <StatusPill status={o.customer_confirmation} label={o.customer_confirmation === "received" ? "Customer confirmed" : "Issue reported"} />}
                  {o.status === "confirmed" && <Money value={o.total} className="font-semibold" />}
                  {o.invoice_no && <Button variant="ghost" size="icon" asChild><Link to={`/invoice/${o.id}`} data-testid={`invoice-link-${o.id}`}><FileText className="h-4 w-4" /></Link></Button>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="ledger">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Details</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
              <TableBody>
                {c.ledger.map((e) => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}><TableCell>{fmtDate(e.date)}</TableCell><TableCell className="capitalize">{e.type}</TableCell><TableCell>{e.note}</TableCell>
                    <TableCell className="text-right">{e.debit ? <Money value={e.debit} /> : ""}</TableCell>
                    <TableCell className="text-right text-emerald-700">{e.credit ? <Money value={e.credit} /> : ""}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="statement"><Statement cid={c.id} /></TabsContent>
        <TabsContent value="messages">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white divide-y" data-testid="customer-messages">
            {(c.messages || []).length === 0 && <div className="p-6 text-sm text-muted-foreground">No automatic WhatsApp messages sent to this customer yet.</div>}
            {(c.messages || []).map((m) => (
              <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`msg-row-${m.id}`}>
                <div><div className="font-semibold">{MSG_KIND[m.kind] || m.kind}</div><div className="text-xs text-muted-foreground">{fmtDateTime(m.created_at)}{m.error ? ` · ${m.error}` : ""}</div></div>
                <WaStatus msg={m} testid={`msg-status-${m.id}`} />
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <PaymentDialog customer={pay} onClose={() => setPay(null)} onSaved={load} />
    </div>
  );
}
